import React, { useEffect, useMemo, useState } from 'react';
import { Spinner, Table } from 'reactstrap';
import SpinnerButton from '../../Common/SpinnerButton';
import BoldButton from '../../Common/BoldButton';
import FilterInput, { textSatisfiesFilter } from '../../FilterInput';
import Confirm from '../../Common/Confirm';
import PaginatedTable from '../../Common/Pagination/PaginatedTable';
import useExpiryStatus from '../../Common/useExpiryStatus';
import SyncExpiryAlert from '../../Common/Sync/SyncExpiryAlert';
import { UseDisplayMessage } from '../../Common/useDisplayMessage';
import useGetPendingTransportSync from '../../../requests/useGetPendingTransportSync';
import useCommitTransportSync from '../../../requests/useCommitTransportSync';
import useGetTransports, { TransportDto } from '../../../requests/useGetTransports';
import useGetSiteTransports from '../../../requests/useGetSiteTransports';
import {
  MergeFieldSelections,
  MergeTarget,
  PendingTransportSyncRecord,
  SyncOperation,
  TransportSyncAction,
} from '../../Common/dtos/PendingTransportSyncDto';
import { TransportSearchModal } from './TransportSearchModal';
import { EditImportModal, getMergeChanges } from './EditImportModal';

const PAGE_SIZE = 250;
const SYNC_EXPIRY_HOURS = 4;
const SYNC_WARNING_HOURS = 3;

interface PendingOperation {
  import: PendingTransportSyncRecord;
  action: TransportSyncAction;
  mergeTarget?: MergeTarget;
  mergeFields?: MergeFieldSelections;
}

interface TransportSyncReviewProps {
  siteId: string;
  siteLabel?: string;
  displayMessage: UseDisplayMessage;
}

const filterByNameOrNumber = (records: PendingTransportSyncRecord[], text: string) => {
  const satisfies = textSatisfiesFilter(text);
  return records.filter(rec => satisfies(rec.name) || satisfies(rec.number ?? ''));
};

const defaultMergeFields = (importRecord: PendingTransportSyncRecord, mergeTarget: MergeTarget): MergeFieldSelections => ({
  updateName: !mergeTarget.name && !!importRecord.name,
  updateNumber: !mergeTarget.number && !!importRecord.number,
  updateMaxWeight: mergeTarget.maxWeight == null && importRecord.maxWeight != null,
  updateCompartments: !mergeTarget.compartments?.length && !!importRecord.compartments?.length,
});

const buildOperationFor = (importRecord: PendingTransportSyncRecord): PendingOperation => {
  const mergeTarget = importRecord.matchedTransport;
  if (!mergeTarget) {
    return { import: importRecord, action: 'Create' };
  }
  return {
    import: importRecord,
    action: 'Merge',
    mergeTarget,
    mergeFields: defaultMergeFields(importRecord, mergeTarget),
  };
};

const SelectCheckbox = ({ checked, onChange, title }: { checked: boolean; onChange: () => void; title?: string }) => (
  <input
    type="checkbox"
    style={{ width: '20px', height: '20px', verticalAlign: 'middle' }}
    title={title}
    checked={checked}
    onChange={onChange}
  />
);

interface ImportToolbarProps {
  onFilterTextChange: (text: string) => void;
  selectedCount?: number;
  importing?: boolean;
  actionsDisabled?: boolean;
  onImport?: () => void;
  onRemoveFromList?: () => void;
}

const ImportToolbar = ({ onFilterTextChange, selectedCount = 0, importing, actionsDisabled, onImport, onRemoveFromList }: ImportToolbarProps) => (
  <div className="d-flex align-items-center gap-2 mb-3">
    <div style={{ width: '280px' }}>
      <FilterInput placeholder="Filter by name or number" onFilterTextChange={onFilterTextChange} />
    </div>
    <div className="ms-auto d-flex gap-2">
      {onRemoveFromList && (
        <BoldButton className="btn-ghost-secondary" onClick={onRemoveFromList} disabled={actionsDisabled}>
          Remove from This List
        </BoldButton>
      )}
      {onImport && (
        <SpinnerButton
          spinning={!!importing}
          className={actionsDisabled ? 'btn-secondary' : 'btn-primary'}
          onClick={onImport}
          disabled={actionsDisabled}
        >
          Import {selectedCount} Record{selectedCount !== 1 ? 's' : ''}
        </SpinnerButton>
      )}
    </div>
  </div>
);

const TabButton = ({ active, onClick, label, count, badgeClass }: { active: boolean; onClick: () => void; label: string; count: number; badgeClass: string }) => (
  <li className="nav-item" role="presentation">
    <button type="button" role="tab" className={`nav-link ${active ? 'active' : ''}`} onClick={onClick}>
      {label}
      {count > 0 && <span className={`ms-1 badge ${badgeClass}`}>{count}</span>}
    </button>
  </li>
);

const TransportSyncReview = ({ siteId, siteLabel, displayMessage }: TransportSyncReviewProps) => {
  const [operations, setOperations] = useState<Map<string, PendingOperation>>(new Map());
  const [operationErrors, setOperationErrors] = useState<Map<string, string>>(new Map());
  const [committedIds, setCommittedIds] = useState<Set<string>>(new Set());
  const [newFilterText, setNewFilterText] = useState('');
  const [mergeFilterText, setMergeFilterText] = useState('');
  const [errorsFilterText, setErrorsFilterText] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'new' | 'merge' | 'errors'>('new');
  const [newPage, setNewPage] = useState(0);
  const [mergePage, setMergePage] = useState(0);
  const [errorsPage, setErrorsPage] = useState(0);
  const [selectingMergeFor, setSelectingMergeFor] = useState<PendingTransportSyncRecord | undefined>();
  const [configuringMerge, setConfiguringMerge] = useState<PendingOperation | undefined>();
  const [showConfirmExecute, setShowConfirmExecute] = useState(false);
  const [showConfirmRemove, setShowConfirmRemove] = useState(false);
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());

  const getPendingSync = useGetPendingTransportSync(siteId, {
    onSuccess: (data) => initializeOperations(data.records),
    onError: () => displayMessage.fail('Failed to load pending transport records.'),
  });

  const getTransports = useGetTransports();

  const getSiteTransports = useGetSiteTransports(siteId);

  const commitSync = useCommitTransportSync(siteId, {
    onSuccess: (results) => {
      const successIds = results.filter(r => r.success).map(r => r.tmxId);
      const failedResults = results.filter(r => !r.success);
      if (failedResults.length > 0) {
        setOperationErrors(prev => {
          const next = new Map(prev);
          failedResults.forEach(r => next.set(r.tmxId, r.error ?? 'Import failed.'));
          return next;
        });
        displayMessage.fail(`${failedResults.length} record(s) failed to import.`);
      } else {
        displayMessage.success(`Successfully imported ${successIds.length} transport(s).`);
      }
      setCommittedIds(prev => new Set([...prev, ...successIds]));
      setOperations(prev => {
        const next = new Map(prev);
        successIds.forEach(id => next.delete(id));
        return next;
      });
      setSelectedIds([]);
    },
    onError: () => displayMessage.fail('Failed to commit sync.'),
  });

  const initializeOperations = (records: PendingTransportSyncRecord[]) => {
    setOperations(previousOperations => {
      const nextOperations = new Map<string, PendingOperation>(previousOperations);
      records.forEach(record => {
        const alreadyTracked = previousOperations.has(record.tmxId);
        if (alreadyTracked || record.takeoverError) return;
        nextOperations.set(record.tmxId, buildOperationFor(record));
      });
      return nextOperations;
    });
  };

  useEffect(() => {
    setSelectedIds([]);
    setNewPage(0);
  }, [newFilterText]);

  useEffect(() => {
    setSelectedIds([]);
    setMergePage(0);
  }, [mergeFilterText]);

  const updateOperation = (tmxId: string, updates: Partial<PendingOperation>) => {
    setOperations(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(tmxId);
      if (existing) newMap.set(tmxId, { ...existing, ...updates });
      return newMap;
    });
  };

  const toggleSelection = (tmxId: string) => {
    setSelectedIds(prev =>
      prev.includes(tmxId) ? prev.filter(x => x !== tmxId) : [...prev, tmxId]
    );
  };

  const toggleSelectAll = (pageRecords: PendingTransportSyncRecord[]) => {
    const pageIds = pageRecords.map(rec => rec.tmxId);
    const allSelected = pageIds.every(id => selectedIds.includes(id));
    setSelectedIds(prev =>
      allSelected ? prev.filter(id => !pageIds.includes(id)) : [...new Set([...prev, ...pageIds])]
    );
  };

  const switchTab = (tab: 'new' | 'merge' | 'errors') => {
    setActiveTab(tab);
    setSelectedIds([]);
    setNewPage(0);
    setMergePage(0);
    setErrorsPage(0);
  };

  const handleMergeTargetSelected = (transport: TransportDto) => {
    if (selectingMergeFor) {
      const mergeTarget: MergeTarget = {
        id: transport.id,
        name: transport.name,
        number: transport.number,
        weightUnit: transport.weightUnit,
        maxWeight: transport.maxWeight,
        compartments: transport.compartments,
      };
      updateOperation(selectingMergeFor.tmxId, {
        action: 'Merge',
        mergeTarget,
        mergeFields: defaultMergeFields(selectingMergeFor, mergeTarget),
      });
    }
    setSelectingMergeFor(undefined);
  };

  const handleCreateInstead = (tmxId: string) => {
    updateOperation(tmxId, { action: 'Create', mergeTarget: undefined, mergeFields: undefined });
    setSelectedIds(prev => prev.filter(id => id !== tmxId));
  };

  const handleExecuteSelected = () => {
    const selectedOps = selectedIds.map(id => operations.get(id)).filter(Boolean) as PendingOperation[];
    const body: SyncOperation[] = selectedOps.map(op => ({
      tmxId: op.import.tmxId,
      action: op.action,
      mergeTargetTransportId: op.mergeTarget?.id,
      mergeFields: op.mergeFields,
    }));
    commitSync.request(body);
    setShowConfirmExecute(false);
  };

  const handleRemoveSelected = () => {
    setRemovedIds(prev => new Set([...prev, ...selectedIds]));
    setSelectedIds([]);
    setShowConfirmRemove(false);
  };

  const siteRecords = (getPendingSync.data?.records ?? []).filter(r => !committedIds.has(r.tmxId) && !removedIds.has(r.tmxId));

  const assignedTransportIds = useMemo(
    () => new Set((getSiteTransports.data ?? []).map(t => t.id)),
    [getSiteTransports.data]
  );

  const errorRecords = useMemo(
    () => siteRecords.filter(rec => !!rec.takeoverError),
    [siteRecords]
  );

  const newRecords = useMemo(
    () => siteRecords.filter(rec => !rec.takeoverError && operations.get(rec.tmxId)?.action === 'Create')
      .sort((a, b) => a.name.localeCompare(b.name)),
    [siteRecords, operations]
  );

  const mergeRecords = useMemo(
    () => siteRecords.filter(rec => !rec.takeoverError && operations.get(rec.tmxId)?.action === 'Merge')
      .sort((a, b) => a.name.localeCompare(b.name)),
    [siteRecords, operations]
  );

  const filteredNew = useMemo(() => filterByNameOrNumber(newRecords, newFilterText), [newRecords, newFilterText]);
  const filteredMerge = useMemo(() => filterByNameOrNumber(mergeRecords, mergeFilterText), [mergeRecords, mergeFilterText]);
  const filteredErrors = useMemo(() => filterByNameOrNumber(errorRecords, errorsFilterText), [errorRecords, errorsFilterText]);

  const newTotalPages = Math.max(1, Math.ceil(filteredNew.length / PAGE_SIZE));
  const pagedNew = filteredNew.slice(newPage * PAGE_SIZE, (newPage + 1) * PAGE_SIZE);
  const allNewPageSelected = pagedNew.length > 0 && pagedNew.every(rec => selectedIds.includes(rec.tmxId));

  const mergeTotalPages = Math.max(1, Math.ceil(filteredMerge.length / PAGE_SIZE));
  const pagedMerge = filteredMerge.slice(mergePage * PAGE_SIZE, (mergePage + 1) * PAGE_SIZE);
  const allMergePageSelected = pagedMerge.length > 0 && pagedMerge.every(rec => selectedIds.includes(rec.tmxId));

  const errorsTotalPages = Math.max(1, Math.ceil(filteredErrors.length / PAGE_SIZE));
  const pagedErrors = filteredErrors.slice(errorsPage * PAGE_SIZE, (errorsPage + 1) * PAGE_SIZE);

  const expiry = useExpiryStatus(getPendingSync.data?.fetchedAt, { expiryHours: SYNC_EXPIRY_HOURS, warningHours: SYNC_WARNING_HOURS });
  const actionsDisabled = selectedIds.length === 0 || commitSync.isLoading || expiry.isExpired;

  const selectedConfirmBody = useMemo(() => {
    const selectedOperations = selectedIds.map(id => operations.get(id)).filter(Boolean) as PendingOperation[];
    const createCount = selectedOperations.filter(op => op.action === 'Create').length;
    const mergeCount = selectedOperations.filter(op => op.action === 'Merge').length;
    const importCount = createCount + mergeCount;
    const transportNoun = (count: number) => count === 1 ? 'transport' : 'transports';

    const createClause = <>create <span className="fw-bold">{createCount}</span> new {transportNoun(createCount)}</>;
    const mergeClause = <>update <span className="fw-bold">{mergeCount}</span> {transportNoun(mergeCount)} in Constellation</>;

    const actionText =
      createCount > 0 && mergeCount > 0
        ? <>This will {createClause} and {mergeClause}.</>
        : createCount > 0
          ? <>This will {createClause}.</>
          : mergeCount > 0
            ? <>This will {mergeClause}.</>
            : null;

    return (
      <>
        {actionText && <p className="mb-3">{actionText}</p>}
        {importCount > 0 && (
          <p className="mb-0"><span className="fw-bold">{importCount}</span> {transportNoun(importCount)} will be assigned to {siteLabel}.</p>
        )}
      </>
    );
  }, [selectedIds, operations, siteLabel]);

  if (getPendingSync.isLoading) {
    return (
      <div className="text-center" style={{ paddingTop: '12rem' }}>
        <Spinner className="mb-3" aria-busy="true" style={{ width: '3rem', height: '3rem' }} />
        <p className="text-muted mb-1">Data received from {siteLabel}.</p>
        <p className="text-muted">Processing…</p>
      </div>
    );
  }

  if (newRecords.length === 0 && mergeRecords.length === 0) {
    return (
      <div className="text-center" style={{ paddingTop: '8rem' }}>
        <i className="bi-check-circle text-success" style={{ fontSize: '3rem' }} />
        <p className="mt-3 fw-semibold">
          {committedIds.size > 0 ? 'All transports imported successfully.' : 'No transports to import for this site.'}
        </p>
        {errorRecords.length > 0 && (
          <p className="text-muted small">{errorRecords.length} transport{errorRecords.length !== 1 ? 's' : ''} could not be imported due to ownership errors in TMX.</p>
        )}
      </div>
    );
  }

  return (
    <>
      <SyncExpiryAlert level={expiry.level} expiresAt={expiry.expiresAt} />

      <Confirm
        visible={showConfirmExecute}
        title="Confirm Import"
        body={selectedConfirmBody}
        isLoading={commitSync.isLoading}
        onDismiss={() => setShowConfirmExecute(false)}
        onConfirm={handleExecuteSelected}
      />

      <Confirm
        visible={showConfirmRemove}
        title="Remove from This List"
        body={
          <>
            <p className="mb-3"><span className="fw-bold">{selectedIds.length}</span> {selectedIds.length === 1 ? 'record' : 'records'} will be removed from this list.</p>
            <p className="mb-0 text-muted small">To import these transports in the future, you will need to sync again.</p>
          </>
        }
        isLoading={false}
        onDismiss={() => setShowConfirmRemove(false)}
        onConfirm={handleRemoveSelected}
      />

      {selectingMergeFor && (
        <TransportSearchModal
          visible={true}
          transports={getTransports.data ?? []}
          assignedTransportIds={assignedTransportIds}
          onSelect={handleMergeTargetSelected}
          onCancel={() => setSelectingMergeFor(undefined)}
        />
      )}

      {configuringMerge && (
        <EditImportModal
          visible={true}
          operation={configuringMerge}
          existingTransports={getTransports.data ?? []}
          onSave={(mergeFields) => {
            updateOperation(configuringMerge.import.tmxId, { mergeFields });
            setConfiguringMerge(undefined);
          }}
          onCreateNew={
            configuringMerge.import.name.toLowerCase() === configuringMerge.mergeTarget?.name.toLowerCase() ||
            (!!configuringMerge.import.number && configuringMerge.import.number.toLowerCase() === configuringMerge.mergeTarget?.number?.toLowerCase())
              ? undefined
              : () => {
                  handleCreateInstead(configuringMerge.import.tmxId);
                  setConfiguringMerge(undefined);
                }
          }
          onCancel={() => setConfiguringMerge(undefined)}
        />
      )}

      <ul className="nav nav-tabs mb-3" role="tablist">
        <TabButton active={activeTab === 'new'} onClick={() => switchTab('new')} label="New" count={newRecords.length} badgeClass="bg-success" />
        <TabButton active={activeTab === 'merge'} onClick={() => switchTab('merge')} label="Merge" count={mergeRecords.length} badgeClass="bg-warning" />
        {errorRecords.length > 0 && (
          <TabButton active={activeTab === 'errors'} onClick={() => switchTab('errors')} label="Cannot Import" count={errorRecords.length} badgeClass="bg-secondary" />
        )}
        <li className="nav-item ms-auto d-flex align-items-center" style={{ paddingRight: '0.75rem' }}>
          <span className="text-muted">Syncing <span className="fw-semibold">{siteLabel}</span></span>
        </li>
      </ul>

      {activeTab === 'new' && (
        <>
          {newRecords.length === 0 && (
            <p className="text-muted">No new transports to import for this site.</p>
          )}
          {newRecords.length > 0 && (
            <>
              <ImportToolbar
                onFilterTextChange={setNewFilterText}
                selectedCount={selectedIds.length}
                importing={commitSync.isLoading}
                actionsDisabled={actionsDisabled}
                onImport={() => setShowConfirmExecute(true)}
                onRemoveFromList={() => setShowConfirmRemove(true)}
              />
              <PaginatedTable
                currentPage={newPage}
                totalPages={newTotalPages}
                pageSize={PAGE_SIZE}
                onPageChange={setNewPage}
              >
                <Table bordered>
                  <thead>
                    <tr>
                      <th className="px-2" style={{ width: '1%', verticalAlign: 'middle' }}>
                        <SelectCheckbox title="Select all on this page" checked={allNewPageSelected} onChange={() => toggleSelectAll(pagedNew)} />
                      </th>
                      <th>Name</th>
                      <th>Number</th>
                      <th style={{ width: '160px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedNew.map(rec => {
                      const isSelected = selectedIds.includes(rec.tmxId);
                      const error = operationErrors.get(rec.tmxId);
                      return (
                        <tr key={rec.tmxId} className={isSelected ? 'sync-row-selected' : 'sync-row-new'}>
                          <td className="px-2 align-middle" style={{ width: '1%' }}>
                            <SelectCheckbox checked={isSelected} onChange={() => toggleSelection(rec.tmxId)} />
                          </td>
                          <td>
                            <div>{rec.name}</div>
                            {error && <div className="small text-danger mt-1">{error}</div>}
                          </td>
                          <td>{rec.number || '-'}</td>
                          <td>
                            <BoldButton
                              className="btn-ghost-secondary btn-sm"
                              onClick={() => setSelectingMergeFor(rec)}
                            >
                              Merge with Existing…
                            </BoldButton>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </PaginatedTable>
            </>
          )}
        </>
      )}

      {activeTab === 'merge' && (
        <>
          {mergeRecords.length === 0 && (
            <p className="text-muted">No transports staged for merge for this site.</p>
          )}
          {mergeRecords.length > 0 && (
            <>
              <p className="text-muted small mb-3">These records were automatically matched to existing Constellation transports by name and number. Review the proposed changes before importing.</p>
              <ImportToolbar
                onFilterTextChange={setMergeFilterText}
                selectedCount={selectedIds.length}
                importing={commitSync.isLoading}
                actionsDisabled={actionsDisabled}
                onImport={() => setShowConfirmExecute(true)}
                onRemoveFromList={() => setShowConfirmRemove(true)}
              />
              <PaginatedTable
                currentPage={mergePage}
                totalPages={mergeTotalPages}
                pageSize={PAGE_SIZE}
                onPageChange={setMergePage}
              >
                <Table bordered>
                  <thead>
                    <tr>
                      <th className="px-2" style={{ width: '1%', verticalAlign: 'middle' }}>
                        <SelectCheckbox title="Select all on this page" checked={allMergePageSelected} onChange={() => toggleSelectAll(pagedMerge)} />
                      </th>
                      <th>TMX Name</th>
                      <th>TMX Number</th>
                      <th>Merging Into</th>
                      <th>Constellation Changes</th>
                      <th style={{ width: '80px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedMerge.map(rec => {
                      const op = operations.get(rec.tmxId);
                      const mergeChanges = getMergeChanges(rec, op?.mergeTarget, op?.mergeFields);
                      const isSelected = selectedIds.includes(rec.tmxId);
                      const error = operationErrors.get(rec.tmxId);
                      return (
                        <tr key={rec.tmxId} className={isSelected ? 'sync-row-selected' : 'sync-row-merge'}>
                          <td className="px-2 align-middle" style={{ width: '1%' }}>
                            <SelectCheckbox checked={isSelected} onChange={() => toggleSelection(rec.tmxId)} />
                          </td>
                          <td>
                            <div>{rec.name}</div>
                            {error && <div className="small text-danger mt-1">{error}</div>}
                          </td>
                          <td>{rec.number || '-'}</td>
                          <td className="text-muted">{op?.mergeTarget?.name ?? '-'}</td>
                          <td>
                            {mergeChanges.length === 0 ? (
                              <span className="text-muted small">No changes</span>
                            ) : (
                              <div className="small">
                                {mergeChanges.map((change, idx) => (
                                  <div key={idx} className="text-muted">
                                    <span className="fw-bold">{change.field}:</span>{' '}
                                    <span className="text-decoration-line-through">{change.from || '(empty)'}</span>
                                    {' → '}
                                    <span className="text-success">{change.to || '(empty)'}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                          <td>
                            <BoldButton
                              className="btn-ghost-secondary btn-sm"
                              onClick={() => op && setConfiguringMerge(op)}
                            >
                              Configure
                            </BoldButton>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </PaginatedTable>
            </>
          )}
        </>
      )}

      {activeTab === 'errors' && (
        <>
          <p className="text-muted small mb-3">TMX is unable to transfer ownership of these transports to Constellation. They cannot be imported until the ownership issue is resolved in TMX.</p>
          <ImportToolbar onFilterTextChange={setErrorsFilterText} />
          <PaginatedTable
            currentPage={errorsPage}
            totalPages={errorsTotalPages}
            pageSize={PAGE_SIZE}
            onPageChange={setErrorsPage}
          >
            <Table bordered>
              <thead>
                <tr>
                  <th>TMX Name</th>
                  <th>TMX Number</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {pagedErrors.map(rec => (
                  <tr key={rec.tmxId} className="sync-row-error">
                    <td>{rec.name}</td>
                    <td>{rec.number || '-'}</td>
                    <td className="small">{rec.takeoverError}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </PaginatedTable>
        </>
      )}
    </>
  );
};

export { TransportSyncReview };
